import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useQueryClient, useMutation } from "react-query";
import { useToast } from "@chakra-ui/react";
import { deleteMethod, put } from "src/api";
import * as mutation from "src/api/affirmation/intentions/mutations";
import { QUERY_KEYS } from "./constants";
import { useDispatch } from "react-redux";
import {
  toggleIsBtn,
  endDateValue,
  toggleBlinkValue,
  setMessage,
  startDateValue,
} from "src/store/media";
import { acceptIntentionWithReset } from "./utils/intentionManager";
import { useIntl } from "react-intl";

/**
 * Per-intention loading states interface
 */
interface IntentionLoadingStates {
  [intentionId: string]: {
    isAccepting?: boolean;
    isDeclining?: boolean;
    isDeleting?: boolean;
  };
}

/**
 * Message context state interface
 */
interface MessageContextState {
  acceptedMessages: Set<string>;
  declinedMessages: Set<string>;
  loadingStates: IntentionLoadingStates;
  errors: { [intentionId: string]: string };
}

/**
 * Message context actions interface
 */
interface MessageContextActions {
  handleAccept: (
    sharedId: number,
    intentionId: string,
    intentionName: string
  ) => Promise<void>;
  handleDecline: (sharedId: number, intentionId: string) => Promise<void>;
  handleDelete: (
    intentionId: string,
    isSubliminal: boolean,
    sharedId?: number,
    isSharedWithMe?: boolean
  ) => Promise<void>;
  isMessageAccepted: (intentionId: string) => boolean;
  isIntentionLoading: (
    intentionId: string,
    action?: "accepting" | "declining" | "deleting"
  ) => boolean;
  clearError: (intentionId: string) => void;
  setAcceptedMessage: (intentionId: string) => void;
  setDeclinedMessage: (intentionId: string) => void;
}

/**
 * Combined context interface
 */
interface MessageContextValue
  extends MessageContextState,
    MessageContextActions {}

/**
 * Context creation
 */
const MessageContext = createContext<MessageContextValue | undefined>(
  undefined
);

/**
 * Provider props interface
 */
interface MessageProviderProps {
  children: ReactNode;
}

/**
 * MessageProvider component that wraps the intentions module
 */
export const MessageProvider: React.FC<MessageProviderProps> = ({
  children,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { formatMessage } = useIntl();

  // State management
  const [acceptedMessages, setAcceptedMessages] = useState<Set<string>>(
    new Set()
  );

  const [declinedMessages, setDeclinedMessages] = useState<Set<string>>(
    new Set()
  );

  const [loadingStates, setLoadingStates] = useState<IntentionLoadingStates>(
    {}
  );
  const [errors, setErrors] = useState<{ [intentionId: string]: string }>({});

  // Mutations
  const { mutateAsync: deleteIntentionMutation } = useMutation(
    mutation.deleteIntention,
    {
      onSuccess: () => queryClient.invalidateQueries(QUERY_KEYS.LIST_INTENTION),
    }
  );

  const { mutateAsync: deleteSubliminalMutation } = useMutation(
    mutation.deleteSubliminal,
    {
      onSuccess: () =>
        queryClient.invalidateQueries(QUERY_KEYS.LIST_SUBLIMINAL),
    }
  );

  // Helper function to update loading state for a specific intention
  const updateLoadingState = useCallback(
    (
      intentionId: string,
      action: "accepting" | "declining" | "deleting",
      isLoading: boolean
    ) => {
      setLoadingStates((prev) => ({
        ...prev,
        [intentionId]: {
          ...prev[intentionId],
          [`is${action.charAt(0).toUpperCase() + action.slice(1)}`]: isLoading,
        },
      }));
    },
    []
  );

  // Helper function to set error for a specific intention
  const setIntentionError = useCallback(
    (intentionId: string, error: string) => {
      setErrors((prev) => ({
        ...prev,
        [intentionId]: error,
      }));
    },
    []
  );

  const clearError = useCallback((intentionId: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[intentionId];
      return newErrors;
    });
  }, []);

  // Actions
  const handleAccept = useCallback(
    async (sharedId: number, intentionId: string, intentionName: string) => {
      try {
        updateLoadingState(intentionId, "accepting", true);
        clearError(intentionId);

        const response = await put(`/shared/${sharedId}/approve`, { sharedId });

        // The API response is processed through handleRes, so we check for success differently
        // Success cases: response exists, has success: true, or is any truthy response (successful API call)
        if (response !== undefined && response !== null) {
          setAcceptedMessages((prev) => new Set(prev).add(intentionId));
          // Optionally invalidate queries to refresh shared data
          queryClient.invalidateQueries("sharedList");

          // Use our utility function for proper intention reset and activation
          acceptIntentionWithReset(dispatch, intentionName);

          // Show success toast
          toast({
            title: formatMessage({ id: "success" }),
            description: formatMessage({ id: "intentionAcceptedSuccessfully" }),
            status: "success",
          });
        } else {
          setIntentionError(
            intentionId,
            formatMessage({ id: "failedToAcceptIntention" })
          );
          toast({
            title: formatMessage({ id: "error" }),
            description: formatMessage({ id: "failedToAcceptIntention" }),
            status: "error",
          });
        }
      } catch (error) {
        console.error("Error accepting message:", error);
        setIntentionError(
          intentionId,
          formatMessage({ id: "failedToAcceptIntention" })
        );
        toast({
          title: formatMessage({ id: "error" }),
          description: formatMessage({ id: "failedToAcceptIntention" }),
          status: "error",
        });
      } finally {
        updateLoadingState(intentionId, "accepting", false);
      }
    },
    [
      updateLoadingState,
      queryClient,
      toast,
      clearError,
      setIntentionError,
      dispatch,
      formatMessage,
    ]
  );

  const handleDecline = useCallback(
    async (sharedId: number, intentionId: string) => {
      try {
        updateLoadingState(intentionId, "declining", true);
        clearError(intentionId);

        const response = await put(`/shared/${sharedId}/reject`, { sharedId });

        // The API response is processed through handleRes, so we check for success differently
        // Success cases: response exists, has success: true, or is any truthy response (successful API call)
        if (response !== undefined && response !== null) {
          // Optionally invalidate queries to refresh shared data
          queryClient.invalidateQueries("sharedList");
          setDeclinedMessages((prev) => new Set(prev).add(intentionId));
          // Show success toast
          toast({
            title: formatMessage({ id: "success" }),
            description: formatMessage({ id: "intentionDeclinedSuccessfully" }),
            status: "success",
          });
        } else {
          setIntentionError(
            intentionId,
            formatMessage({ id: "failedToDeclineIntention" })
          );
          toast({
            title: formatMessage({ id: "error" }),
            description: formatMessage({ id: "failedToDeclineIntention" }),
            status: "error",
          });
        }
      } catch (error) {
        console.error("Error declining message:", error);
        setIntentionError(
          intentionId,
          formatMessage({ id: "failedToDeclineIntention" })
        );
        toast({
          title: formatMessage({ id: "error" }),
          description: formatMessage({ id: "failedToDeclineIntention" }),
          status: "error",
        });
      } finally {
        updateLoadingState(intentionId, "declining", false);
      }
    },
    [
      updateLoadingState,
      queryClient,
      toast,
      clearError,
      setIntentionError,
      formatMessage,
    ]
  );

  const handleDelete = useCallback(
    async (
      intentionId: string,
      isSubliminal: boolean,
      sharedId?: number,
      isSharedWithMe?: boolean
    ) => {
      try {
        updateLoadingState(intentionId, "deleting", true);
        clearError(intentionId);

        if (isSharedWithMe && sharedId) {
          const response = await put(`/shared/${sharedId}/reject`, {
            sharedId,
          });

          if (response !== undefined && response !== null) {
            // Remove from the user's view immediately
            setDeclinedMessages((prev) => new Set(prev).add(intentionId));
            queryClient.invalidateQueries("sharedList");
            toast({
              title: formatMessage({ id: "success" }),
              description: formatMessage({
                id: "sharedIntentionRemovedSuccessfully",
              }),
              status: "success",
            });
          } else {
            throw new Error("Failed to remove shared intention");
          }
        } else {
          // For user-owned intentions, use the existing delete mutation
          const deleteMutation = isSubliminal
            ? deleteSubliminalMutation
            : deleteIntentionMutation;
          const numericId = Number(intentionId);

          await deleteMutation(numericId);
        }
      } catch (error) {
        console.error("Error deleting message:", error);
        const errorMessage = isSharedWithMe
          ? formatMessage({ id: "failedToRemoveSharedIntention" })
          : formatMessage({ id: "failedToDeleteMessage" });
        setIntentionError(intentionId, errorMessage);
        toast({
          title: formatMessage({ id: "error" }),
          description: errorMessage,
          status: "error",
        });
      } finally {
        updateLoadingState(intentionId, "deleting", false);
      }
    },
    [
      updateLoadingState,
      deleteIntentionMutation,
      deleteSubliminalMutation,
      queryClient,
      toast,
      clearError,
      setIntentionError,
      setDeclinedMessages,
      formatMessage,
    ]
  );

  const isMessageAccepted = useCallback(
    (intentionId: string) => {
      return acceptedMessages.has(intentionId);
    },
    [acceptedMessages]
  );

  const isIntentionLoading = useCallback(
    (
      intentionId: string,
      action?: "accepting" | "declining" | "deleting"
    ): boolean => {
      const intentionLoadingState = loadingStates[intentionId];
      if (!intentionLoadingState) return false;

      if (action) {
        const loadingKey = `is${
          action.charAt(0).toUpperCase() + action.slice(1)
        }` as keyof typeof intentionLoadingState;
        return Boolean(intentionLoadingState[loadingKey]);
      }

      // Return true if any action is loading for this intention
      return Boolean(
        intentionLoadingState.isAccepting ||
          intentionLoadingState.isDeclining ||
          intentionLoadingState.isDeleting
      );
    },
    [loadingStates]
  );

  const setAcceptedMessage = useCallback((intentionId: string) => {
    setAcceptedMessages((prev) => new Set(prev).add(intentionId));
  }, []);

  const setDeclinedMessage = useCallback((intentionId: string) => {
    setDeclinedMessages((prev) => new Set(prev).add(intentionId));
  }, []);

  const contextValue: MessageContextValue = {
    // State
    acceptedMessages,
    declinedMessages,
    loadingStates,
    errors,
    // Actions
    handleAccept,
    handleDecline,
    handleDelete,
    isMessageAccepted,
    isIntentionLoading,
    clearError,
    setAcceptedMessage,
    setDeclinedMessage,
  };

  return (
    <MessageContext.Provider value={contextValue}>
      {children}
    </MessageContext.Provider>
  );
};

/**
 * Custom hook to use the Message context
 */
export const useMessageContext = (): MessageContextValue => {
  const context = useContext(MessageContext);
  if (context === undefined) {
    throw new Error("useMessageContext must be used within a MessageProvider");
  }
  return context;
};

/**
 * Hook for message actions only (cleaner API for components that only need actions)
 */
export const useMessageActions = () => {
  const { handleAccept, handleDecline, handleDelete, isMessageAccepted } =
    useMessageContext();

  return {
    handleAccept,
    handleDecline,
    handleDelete,
    isMessageAccepted,
  };
};

/**
 * Hook for loading states only
 */
export const useIntentionLoadingStates = () => {
  const { isIntentionLoading, loadingStates } = useMessageContext();

  return {
    isIntentionLoading,
    loadingStates,
  };
};
